const NAME_PATTERN = /^[\p{L}\s'.-]+$/u;
const FREE_TEXT_PATTERN = /^[\p{L}\s'.\-/,]+$/u;
const REFERRAL_PATTERN = /^[A-Za-z0-9_-]{2,40}$/;
const NICKNAME_PATTERN = /^[A-Za-z][A-Za-z0-9_]{2,15}$/;
const RESERVED_USERNAMES = new Set([
  "admin",
  "administrator",
  "godo",
  "godocm",
  "support",
  "official",
  "moderator",
  "system",
  "null",
  "undefined",
  "trader",
  "test",
]);
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ACCOUNT_PATTERN = /^[A-Za-z0-9]{4,20}$/;

const isBlank = (value) => !String(value || "").trim();

export const normalizeAccountNumber = (value) =>
  String(value || "").trim().replace(/\s+/g, "");

export const normalizeEmail = (value) => String(value || "").trim().toLowerCase();

export const normalizePhoneDigits = (value) => String(value || "").replace(/\D/g, "");

export const validateRecoveryEmail = (value) => {
  if (isBlank(value)) return "signup.errors.emailRequired";
  if (!EMAIL_PATTERN.test(String(value).trim())) return "signup.errors.emailInvalid";
  return "";
};

export const validateRecoveryPhone = (value) => {
  const phoneDigits = normalizePhoneDigits(value);
  if (!phoneDigits) return "signup.errors.phoneRequired";
  if (phoneDigits.length < 7 || phoneDigits.length > 15) {
    return "signup.errors.phoneInvalid";
  }
  return "";
};

export const validateAccountNumber = (value) => {
  const normalized = normalizeAccountNumber(value);
  if (!normalized) return "signup.errors.accountRequired";
  if (!ACCOUNT_PATTERN.test(normalized)) {
    return "signup.errors.accountInvalid";
  }
  return "";
};

export const validateFullName = (value) => {
  const name = String(value || "").trim().replace(/\s+/g, " ");
  if (!name) return "signup.errors.fullNameRequired";
  if (name.length < 2) return "signup.errors.fullNameShort";
  if (!NAME_PATTERN.test(name)) return "signup.errors.fullNameLetters";
  return "";
};

export const validateEmailField = (value) => {
  if (isBlank(value)) return "signup.errors.emailRequired";
  if (!EMAIL_PATTERN.test(String(value).trim())) return "signup.errors.emailInvalid";
  return "";
};

export const validatePhoneField = (value) => {
  const phoneDigits = normalizePhoneDigits(value);
  if (!phoneDigits) return "signup.errors.phoneRequired";
  if (phoneDigits.length < 7 || phoneDigits.length > 15) {
    return "signup.errors.phoneInvalid";
  }
  return "";
};

export const validateFreeText = (value, requiredKey, lettersKey) => {
  const text = String(value || "").trim();
  if (!text) return requiredKey;
  if (!FREE_TEXT_PATTERN.test(text) || /\d/.test(text)) return lettersKey;
  return "";
};

export const validateNickname = (value, { required = false } = {}) => {
  const nickname = String(value || "").trim().replace(/\s+/g, "");
  if (!nickname) return required ? "signup.errors.nicknameRequired" : "";
  if (nickname.length < 3 || nickname.length > 16) return "signup.errors.nicknameLength";
  if (!NICKNAME_PATTERN.test(nickname)) return "signup.errors.nicknameInvalid";
  if (RESERVED_USERNAMES.has(nickname.toLowerCase())) return "signup.errors.nicknameReserved";
  return "";
};

export const validateReferralCode = (value) => {
  const code = String(value || "").trim();
  if (!code) return "signup.errors.referral";
  if (!REFERRAL_PATTERN.test(code)) return "signup.errors.referralInvalid";
  return "";
};

/** Validate a single field after the user leaves it (blur). */
export const validateSignupField = (key, form) => {
  switch (key) {
    case "fullName":
      return validateFullName(form.fullName);
    case "email":
      return validateEmailField(form.email);
    case "phone":
      return validatePhoneField(form.phone);
    case "countryOfResidence":
      return isBlank(form.countryOfResidence) ? "signup.errors.countryRequired" : "";
    case "nationality":
      return isBlank(form.nationality) ? "signup.errors.nationalityRequired" : "";
    case "profession":
      return isBlank(form.profession) ? "signup.errors.professionRequired" : "";
    case "professionOther":
      return form.profession === "other"
        ? validateFreeText(
            form.professionOther,
            "signup.errors.professionOther",
            "signup.errors.professionOtherLetters",
          )
        : "";
    case "sourceOfFunds":
      return isBlank(form.sourceOfFunds) ? "signup.errors.fundsRequired" : "";
    case "sourceOfFundsOther":
      return form.sourceOfFunds === "other"
        ? validateFreeText(
            form.sourceOfFundsOther,
            "signup.errors.fundsOther",
            "signup.errors.fundsOtherLetters",
          )
        : "";
    case "proofOfIdentity":
      return form.proofOfIdentity ? "" : "signup.errors.identityRequired";
    case "proofOfIdentityBack":
      return form.proofOfIdentityBack ? "" : "signup.errors.identityBackRequired";
    case "proofOfAddress":
      return form.proofOfAddress ? "" : "signup.errors.addressRequired";
    case "usCitizen":
      return form.usCitizen ? "" : "signup.errors.usCitizenRequired";
    case "nickname":
      return validateNickname(form.nickname, { required: true });
    case "referralCode":
      return form.hasReferralCode ? validateReferralCode(form.referralCode) : "";
    case "beneficialOwner":
      return form.beneficialOwner ? "" : "signup.errors.beneficialOwner";
    case "termsAccepted":
      return form.termsAccepted ? "" : "signup.errors.terms";
    case "competitionTermsAccepted":
      return form.competitionTermsAccepted ? "" : "signup.errors.competitionTerms";
    default:
      return "";
  }
};

export const validateSignupFields = (form, onlyKeys = null) => {
  const keys = onlyKeys || [
    "fullName",
    "nickname",
    "email",
    "phone",
    "countryOfResidence",
    "nationality",
    "profession",
    "professionOther",
    "sourceOfFunds",
    "sourceOfFundsOther",
    "proofOfIdentity",
    "proofOfIdentityBack",
    "proofOfAddress",
    "usCitizen",
    "referralCode",
  ];

  const errors = {};
  keys.forEach((key) => {
    const message = validateSignupField(key, form);
    if (message) errors[key] = message;
  });
  return errors;
};

export const validateSignupTerms = (form) => {
  const errors = {};
  const beneficialOwner = validateSignupField("beneficialOwner", form);
  const termsAccepted = validateSignupField("termsAccepted", form);
  const competitionTermsAccepted = validateSignupField("competitionTermsAccepted", form);
  if (beneficialOwner) errors.beneficialOwner = beneficialOwner;
  if (termsAccepted) errors.termsAccepted = termsAccepted;
  if (competitionTermsAccepted) errors.competitionTermsAccepted = competitionTermsAccepted;
  return errors;
};

export const validateSignupForm = (form) => ({
  ...validateSignupFields(form),
  ...validateSignupTerms(form),
});
